'use client'

import { useLocale, useTranslations } from 'next-intl'

import { ModelsNews } from '@/api/generated-api'
import { TaskBackLink } from '@/app/[locale]/tasks/components/TaskBackLink'
import LinkChainIcon from '@/assets/icons/link-chain.svg'
import { showToast } from '@/providers/ToastProvider/ToastMessage'

import NewsCategoryBadge from '../../../components/NewsHeroFeatured/NewsCategoryBadge'
import NewsPageHero from '../../../components/NewsPageHero'
import { formatNewsRelativeTime, getNewsDisplayDate } from '../../../utils/date'
import { getHeaderBannerUrl } from '../../../utils/newsCtaFields'
import { getPrimaryNewsTagLabel } from '../../../utils/newsTags'
import ArticleHeaderBanner from '../ArticleHeaderBanner'
import S from './HeroSection.module.scss'

// The gap only exists to be empty: with a banner the banner itself sets it.
const HERO_TOP_OFFSET = 177
const HERO_TOP_OFFSET_MOBILE = 34

interface HeroSectionProps {
  news: ModelsNews
  onGoBack?: () => void
}

const HeroSection = ({ news, onGoBack }: HeroSectionProps) => {
  const t = useTranslations('news')
  const locale = useLocale()

  const hasBanner = Boolean(getHeaderBannerUrl(news))
  const tagLabel = getPrimaryNewsTagLabel(news)
  const timeLabel = formatNewsRelativeTime(getNewsDisplayDate(news), locale)

  const handleCopyLink = async () => {
    const url = window.location.href

    try {
      await navigator.clipboard.writeText(url)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.append(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }

    showToast({
      type: 'success',
      title: t('detail.copyLink.copied')
    })
  }

  return (
    <NewsPageHero
      contentTopOffset={hasBanner ? 0 : HERO_TOP_OFFSET}
      contentTopOffsetMobile={hasBanner ? 0 : HERO_TOP_OFFSET_MOBILE}
      topSlot={hasBanner ? <ArticleHeaderBanner news={news} /> : undefined}
    >
      <div className={S.heroInner}>
        <div className={S.heroContent}>
          <div className={S.heroControls}>
            <TaskBackLink
              className={S.backLink}
              onClick={onGoBack}
              ariaLabel={t('detail.backLink.ariaLabel')}
              label={t('detail.backLink.mobileLabel')}
            />
            <div className={S.metaRow}>
              <div className={S.metaLeft}>
                {tagLabel && (
                  <NewsCategoryBadge
                    label={tagLabel}
                    className={S.categoryBadge}
                  />
                )}
                {timeLabel && (
                  <time className={S.time} dateTime={getNewsDisplayDate(news)}>
                    {timeLabel}
                  </time>
                )}
              </div>
              <button
                type="button"
                className={S.copyLinkButton}
                onClick={handleCopyLink}
                aria-label={t('detail.copyLink.ariaLabel')}
              >
                <span className={S.copyLinkLabel}>
                  {t('detail.copyLink.label')}
                </span>
                <LinkChainIcon className={S.copyLinkIcon} aria-hidden />
              </button>
            </div>
          </div>
          <h1 className={S.heroTitle}>{news.title}</h1>
        </div>
      </div>
    </NewsPageHero>
  )
}

export default HeroSection
